import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import { MutationFetchFunction } from 'common/helpers/common';
import { ApiError, ApiResult } from 'common/repositories/common.model';
import { queryClient } from 'common/repositories/query-client';

import {
  EmployeeModel,
  EmployeeChangePasswordInput,
  EmployeeMutationInput,
  EmployeeUpdateInput,
  EmployeeDeleteInput,
  EmployeePromotionMutationInput,
  EmployeePromotionUpdateMutationInput,
  EmployeePromotionDeleteMutationInput,
} from './model';
import { getEmployeeKey, getEmployeesKey } from './query';

const ENDPOINT = 'employees';

export function useCreateEmployee(
  options?: UseMutationOptions<
    ApiResult<EmployeeModel>,
    ApiError,
    EmployeeMutationInput
  >,
): UseMutationResult<
  ApiResult<EmployeeModel>,
  ApiError,
  EmployeeMutationInput
> {
  return useMutation<ApiResult<EmployeeModel>, ApiError, EmployeeMutationInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: ENDPOINT,
        method: 'POST',
        classType: EmployeeModel,
        body,
      });
    },
    options,
  );
}

export function useUpdateEmployee(
  options?: UseMutationOptions<
    ApiResult<EmployeeModel>,
    ApiError,
    EmployeeUpdateInput
  >,
): UseMutationResult<ApiResult<EmployeeModel>, ApiError, EmployeeUpdateInput> {
  return useMutation<ApiResult<EmployeeModel>, ApiError, EmployeeUpdateInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `${ENDPOINT}/${body.employeeId}`,
        method: 'PUT',
        classType: EmployeeModel,
        body: body.body,
      });
    },
    options,
  );
}

export function useDeleteEmployee(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    EmployeeDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, EmployeeDeleteInput> {
  return useMutation<{ message: string }, ApiError, EmployeeDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `${ENDPOINT}/${body.employeeId}`,
        method: 'DELETE',
      });
    },
    options,
  );
}

export function useChangePassword(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    EmployeeChangePasswordInput
  >,
): UseMutationResult<
  { message: string },
  ApiError,
  EmployeeChangePasswordInput
> {
  return useMutation<
    { message: string },
    ApiError,
    EmployeeChangePasswordInput
  >(async function (body) {
    return await MutationFetchFunction({
      url: 'me/auth/change-password',
      method: 'POST',
      body,
    });
  }, options);
}

export function useAuthRevoke(
  options?: UseMutationOptions<{ message: string }, ApiError>,
): UseMutationResult<{ message: string }, ApiError> {
  return useMutation<{ message: string }, ApiError>(async function () {
    return await MutationFetchFunction({
      url: 'auth/revoke',
      method: 'POST',
    });
  }, options);
}

const PROMOTION_ENDPOINT = 'promotions';

export function useCreateEmployeePromotion(
  employeeId: string,
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    EmployeePromotionMutationInput
  >,
): UseMutationResult<
  { message: string },
  ApiError,
  EmployeePromotionMutationInput
> {
  return useMutation(
    async function (body) {
      return await MutationFetchFunction({
        url: `${ENDPOINT}/${employeeId}/${PROMOTION_ENDPOINT}`,
        method: 'POST',
        body,
      });
    },
    {
      ...options,
      onSuccess(data, variables, context) {
        queryClient.refetchQueries(getEmployeeKey({ id: employeeId }));
        queryClient.refetchQueries(getEmployeesKey());
        options?.onSuccess?.(data, variables, context);
      },
    },
  );
}

export function useUpdateEmployeePromotion(
  employeeId: string,
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    EmployeePromotionUpdateMutationInput
  >,
): UseMutationResult<
  { message: string },
  ApiError,
  EmployeePromotionUpdateMutationInput
> {
  return useMutation(
    async function ({ id, body }) {
      return await MutationFetchFunction({
        url: `${ENDPOINT}/${employeeId}/${PROMOTION_ENDPOINT}/${id}`,
        method: 'PUT',
        body,
      });
    },
    {
      ...options,
      onSuccess(data, variables, context) {
        queryClient.refetchQueries(getEmployeeKey({ id: employeeId }));
        queryClient.refetchQueries(getEmployeesKey());
        options?.onSuccess?.(data, variables, context);
      },
    },
  );
}

export function useDeleteEmployeePromotion(
  employeeId: string,
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    EmployeePromotionDeleteMutationInput
  >,
): UseMutationResult<
  { message: string },
  ApiError,
  EmployeePromotionDeleteMutationInput
> {
  return useMutation(
    async function ({ id }) {
      return await MutationFetchFunction({
        url: `${ENDPOINT}/${employeeId}/${PROMOTION_ENDPOINT}/${id}`,
        method: 'DELETE',
      });
    },
    {
      ...options,
      onSuccess(data, variables, context) {
        queryClient.refetchQueries(getEmployeeKey({ id: employeeId }));
        queryClient.refetchQueries(getEmployeesKey());
        options?.onSuccess?.(data, variables, context);
      },
    },
  );
}
